import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "./Navbar/Navbar";
import { motion } from "framer-motion";
import { BsCheckCircleFill } from "react-icons/bs";
import { FaDownload, FaCertificate, FaCheckCircle } from "react-icons/fa";
import { toast } from "react-toastify";
import { useAuth } from "../hooks/useAuth";

const STEPS = [
  "Choose Module",
  "Select Certification",
  "Attend Quiz",
  "Google Review",
  "Download Certificate",
];

const UserCertificate = () => {
  const { emailAddress } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [steps, setSteps] = useState(
    location.state?.steps || [true, true, true, true, true],
  );
  const [isRequesting, setIsRequesting] = useState(false);
  const [certificateRequested, setCertificateRequested] = useState(false);

  const { certification, score, totalQuestions } = location.state || {};

  const handleCertificateRequest = async () => {
    setIsRequesting(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      // we need to call the actual API to request certificate here
      setCertificateRequested(true);

      toast.success(
        "Certificate request submitted successfully! Your certificate will be sent to your registered email within 2-3 hours after validation.",
        {
          position: "bottom-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
        },
      );

      setSteps([true, true, true, true, true]);

      sessionStorage.setItem(`certificate_requested_${certification}`, "true");

      setTimeout(() => {
        navigate("/home");
      }, 5000);
    } catch (error) {
      toast.error("Failed to request certificate. Please try again.", {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setIsRequesting(false);
    }
  };

  const breadcrumbVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-100/90 via-white/70 to-red-100/90">
      <Navbar />

      <div className="container mx-auto px-4 py-8 sm:py-16">
        {/* STEPPER */}
        <div className="flex justify-center mb-4 px-2">
          <div className="flex items-center bg-white/70 backdrop-blur-md shadow-xl rounded-full p-2 flex-wrap gap-2">
            {STEPS.map((stepLabel, index) => (
              <motion.div
                key={index}
                className="flex items-center"
                initial="initial"
                animate="animate"
                variants={breadcrumbVariants}
                transition={{ duration: 0.4, delay: index * 0.15 }}
              >
                {/* STEP PILL */}
                <div
                  className={`flex items-center px-4 py-2 rounded-full text-xs sm:text-sm md:text-base font-medium transition-all duration-300 ${
                    steps[index]
                      ? "bg-green-500 text-white"
                      : index === 0
                        ? "bg-gradient-to-r from-red-500 to-red-700 text-white shadow-md"
                        : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {/* STEP CIRCLE */}
                  <span
                    className={`mr-2 flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                      steps[index]
                        ? "bg-white text-green-500"
                        : index === 0
                          ? "bg-white text-red-600"
                          : "bg-gray-300 text-gray-700"
                    }`}
                  >
                    {steps[index] ? <BsCheckCircleFill /> : index + 1}
                  </span>

                  {/* LABEL */}
                  <span className="whitespace-nowrap">{stepLabel}</span>
                </div>

                {/* CONNECTOR LINE */}
                {index < STEPS.length - 1 && (
                  <div className="hidden md:block w-6 h-[2px] bg-gray-300 mx-1" />
                )}
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
        >
          {!certificateRequested ? (
            <div className="bg-white rounded-2xl p-6 sm:p-10 border border-yellow-500/50 shadow-[0_12px_40px_rgba(0,0,0,0.25)]">
              <div className="text-center mb-10">
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", duration: 0.6 }}
                  className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full mb-4"
                >
                  <FaCertificate className="text-blue-500 text-5xl" />
                </motion.div>
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">
                  You're Almost There! 🎉
                </h1>
                <p className="text-lg text-gray-600">
                  Request your certificate for
                </p>
                <p className="text-xl font-semibold text-blue-700 mt-2">
                  {certification?.toUpperCase()}
                </p>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-yellow-500/20 rounded-xl p-6 mb-8">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
                  <div>
                    <p className="text-gray-600 text-xs uppercase mb-1">
                      Quiz Score
                    </p>
                    <p className="text-3xl font-bold text-green-600">
                      {score}/{totalQuestions}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600 text-xs uppercase mb-1">
                      Status
                    </p>
                    <div className="flex items-center justify-center gap-2">
                      <FaCheckCircle className="text-green-600 text-xl" />
                      <p className="text-xl font-bold text-green-600">Passed</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-gray-600 text-xs uppercase mb-1">
                      Registered Email
                    </p>
                    <p className="text-m font-semibold text-gray-800 truncate">
                      {emailAddress}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <FaCertificate className="text-blue-500" />
                  What happens next?
                </h3>
                <ul className="space-y-3 text-gray-700 text-sm">
                  <li className="flex gap-2">
                    <span className="text-blue-600 font-bold">1.</span>
                    <span>Click the "Request Certificate" button below</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-blue-600 font-bold">2.</span>
                    <span>Your certificate will be validated by our team</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-blue-600 font-bold">3.</span>
                    <span>
                      You'll receive your certificate via email within 2-3 hours
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-blue-600 font-bold">4.</span>
                    <span>
                      Check your spam folder if you don't see it in your inbox
                    </span>
                  </li>
                </ul>
              </div>

              <button
                onClick={handleCertificateRequest}
                disabled={isRequesting}
                className={`w-full flex items-center justify-center gap-3 py-4 px-6 rounded-lg font-bold text-lg transition-all duration-300 ${
                  isRequesting
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                }`}
              >
                <FaDownload className="text-2xl" />
                {isRequesting ? "Processing..." : "Request Certificate"}
              </button>

              <p className="text-center text-xs text-gray-500 mt-4">
                By requesting this certificate, you confirm that all information
                provided is accurate.
              </p>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.4)] p-6 sm:p-10 text-center border border-green-500/20"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.2 }}
                className="inline-flex items-center justify-center w-32 h-32 bg-green-100 rounded-full mb-6"
              >
                <BsCheckCircleFill className="text-green-500 text-6xl" />
              </motion.div>

              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                Request Submitted! ✅
              </h2>

              <p className="text-lg text-gray-600 mb-6">
                Your certificate request has been submitted successfully.
              </p>

              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                <p className="text-gray-700 mb-2">
                  📧 Certificate will be sent to:
                </p>
                <p className="text-xl font-semibold text-green-600">
                  {emailAddress}
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-700">
                  ⏱️ Estimated delivery time: <strong>2-3 hours</strong>
                </p>
              </div>

              <button
                onClick={() => navigate("/home")}
                className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-8 rounded-lg font-semibold transition-all duration-300"
              >
                Back to Home
              </button>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default UserCertificate;
